<i18n src="@/locales/modules/DepartmentTranslate.json"></i18n>

<template>
	<el-dialog
		append-to-body
		destroy-on-close
		:before-close="setDialogHide => handleCloseDialog(setDialogHide, isFormDirty)"
		class="custom-dialog"
		:title="$t(dataId ? 'UpdateDepartment' : 'AddNewDepartment')"
		:visible.sync="isPopupActiveLocal"
	>
		<PerfectScrollbar ref="formScrollBar" class="scroll-area--data-list" :settings="settings">
			<div class="p-6">
				<!-- NAME -->
				<label for class="vs-input--label">
					{{ $t("DepartmentName") }}
					<span class="text-danger">*</span>:
				</label>
				<vs-input v-model="dataName" :placeholder="$t('Placeholder.InputName')" class="w-full" name="name" :data-vv-as="$t('DepartmentName')" v-validate="'required'" />
				<span class="text-danger text-sm" v-show="errors.has('name')">{{ errors.first("name") }}</span>

				<!-- Code -->
				<div class="mt-5">
					<label for class="vs-input--label">{{ $t("Code") }}:</label>
					<vs-input v-model="dataCode" class="w-full" name="code" :data-vv-as="$t('Code')" v-validate="'max:10'" :placeholder="$t('Placeholder.InputCode')" />
					<span class="text-danger text-sm" v-show="errors.has('code')">{{ errors.first("code") }}</span>
				</div>

				<div class="mt-5">
					<label class="vs-input--label">{{ $t("ParentDepartment") }}:</label>
					<v-select
						class="select-tree"
						:placeholder="`${$t('Actions.Choose')} ${$t('Department').toLowerCase()}`"
						v-model="parentDepartmentId"
						:reduce="department => department.value"
						label="text"
						name="department"
						:options="departmentChoices"
					>
						<template v-slot:option="option">
							<div :class="`flex items-center pl-${option.depth > 1 ? option.depth * 2 : option.depth}`">
								<i class="mr-2 nucleo nucleo-shape-oval-3"></i>
								{{ option.text }}
							</div>
						</template>
						<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
					</v-select>
				</div>

				<!-- Description -->
				<div class="mt-5">
					<label for class="vs-input--label">{{ $t("Description") }}:</label>
					<vs-textarea :placeholder="$t('Placeholder.InputDescription')" v-model="description" />
				</div>
				<!-- List Titles -->
				<div v-if="data.titles && data.titles.length > 0">
					<label class="text-solid-gray-1">{{ $t("List") }} {{ $t("Title").toLowerCase() }}:</label>
					<AddTitle @onDelete="handleDeleteTitle" v-model="titles" />
				</div>
			</div>
		</PerfectScrollbar>

		<div class="flex justify-center" slot="footer">
			<vs-button :type="data.id ? 'filled' : 'border'" class="w-full sm:w-1/3 mb-3 sm:mb-0 sm:mr-6 font-bold" @click.stop="submitData(!!data.id)">
				{{ data.id ? $t("Actions.Save") : $t("Actions.SaveAndRecreate") }}
			</vs-button>
			<vs-button v-show="!data.id" class="w-full sm:w-1/3 font-bold" @click="submitData">{{ $t("Actions.Submit") }}</vs-button>
		</div>
	</el-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import vSelect from "vue-select";
import AddTitle from "@/components/add-title-with-role-for-department/AddTitle";
import * as _ from "lodash";
import { showConfirm, detectLeavePage } from "@/mixins/mixins.js";

export default {
	provide() {
		return { parentValidator: this.$validator };
	},
	mixins: [showConfirm, detectLeavePage("isFormDirty")],

	props: {
		isPopupActive: {
			type: Boolean,
			required: true
		},
		data: {
			type: Object,
			default: () => {}
		}
	},
	components: {
		vSelect,
		AddTitle
	},
	data() {
		return {
			dataId: null,
			dataName: "",
			parentDepartmentId: null,
			description: "",
			dataCode: "",
			titles: [],

			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			},
			initFormData: {},
			isFormDirty: false
		};
	},
	watch: {
		isPopupActive(val) {
			if (!val) return;

			const { id, name, parent_id, code, description, titles } = this.data;
			this.parentDepartmentId = parent_id || null;
			if (!this.data.id) {
				this.initValues();
			} else {
				this.dataId = id;
				this.dataName = name;
				this.dataCode = code;
				this.description = description;
				this.titles = titles;
			}

			this.isFormDirty = false;
			this.initFormData = {
				dataId: this.dataId,
				dataName: this.dataName,
				dataCode: this.dataCode,
				parentDepartmentId: this.parentDepartmentId,
				description: this.description,
				titles: this.titles
			};
		},
		computedFormData: {
			handler: function(newVal) {
				this.isFormDirty = JSON.stringify(newVal) !== JSON.stringify(this.initFormData) && newVal.dataId === this.initFormData.dataId;
			},
			deep: true
		}
	},
	methods: {
		initValues() {
			if (this.data.id) return;
			this.dataId = null;
			this.dataName = "";
			this.parentDepartmentId = null;
			this.description = "";
			this.dataCode = "";
			this.titles = [];

			this.$validator.reset();
		},
		handleCreateOrUpdateSuccess() {
			this.initValues();
			if (this.dataId) {
				this.$emit("closePopup");
			}
			this.isFormDirty = false;
			this.$helper.notifySuccess({
				context: this,
				options: {
					text: this.$t(`${this.dataId ? "UpdateDepartmentSuccessful" : "CreateDepartmentSuccessful"}`)
				}
			});
		},
		async handleCreateOrUpdateDepartment(dataObj) {
			try {
				this.$vs.loading();
				if (this.dataId) {
					try {
						const list_titles = this.titles.map(title => {
							let item = {
								name: title.name,
								role_id: title.role.id,
								department_id: this.dataId
							};
							if (title.id) {
								item.id = title.id;
							}
							return item;
						});
						await this.$store.dispatch("departmentManagement/updateDepartment", {
							...dataObj,
							id: this.dataId,
							list_titles
						});
					} catch (error) {
						this.$vs.loading.close();
						console.error("DU", error);
					}
				} else {
					await this.$store.dispatch("departmentManagement/addDepartment", dataObj);
				}

				this.handleCreateOrUpdateSuccess();
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$vs.loading.close();
			}
		},
		submitData() {
			this.$validator.validateAll().then(result => {
				if (result) {
					const dataObj = {
						name: this.dataName,
						parent_id: this.parentDepartmentId,
						code: this.dataCode,
						description: this.description
					};
					this.handleCreateOrUpdateDepartment(dataObj);
				}
			});
		},
		async handleDeleteTitle(titleItem = {}) {
			try {
				if (titleItem.id) {
					this.mixinShowConfirm().then(async isAccept => {
						if (isAccept) {
							await this.$store.dispatch("titleManagement/deleteTitle", { id: titleItem.id });
							this.titles = this.titles.filter(title => title.id !== titleItem.id);
							this.initTitles = _.cloneDeep(this.titles);
							this.$helper.notifySuccess({
								context: this,
								options: {
									text: this.$t("DeleteTitleSuccessful")
								}
							});
							this.$emit("onUpdateTitleSuccess");
						}
					});
				}
			} catch (error) {
				console.error("Delet TItle at Department popup", error);
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		}
	},
	computed: {
		isPopupActiveLocal: {
			get() {
				return this.isPopupActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closePopup");
				}
			}
		},

		departmentChoices() {
			return (this.getFormattedFlattenTreeDepartments || []).map(department => ({
				value: department.id,
				text: department.name,
				depth: department.depth
			}));
		},
		...mapGetters({
			getFormattedFlattenTreeDepartments: "departmentManagement/getFormattedFlattenTreeDepartments"
		}),
		computedFormData() {
			return {
				dataId: this.dataId,
				dataName: this.dataName,
				dataCode: this.dataCode,
				parentDepartmentId: this.parentDepartmentId,
				description: this.description,
				titles: this.titles
			};
		}
	}
};
</script>
